import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import PortfolioShell from './PortfolioShell';
import NoteViews from './NoteViews';
import {
  projects,
  notes,
  themes,
  skillGroups,
  getProject,
  getNote,
  getProjectNotes,
} from '../../content/portfolioContent';
import terrainWorkbenchImage from '../../images/world-authoring-terrain-workbench.webp';
import terrainLandscapeImage from '../../images/world-authoring-terrain-landscape.webp';
import terrainPaletteImage from '../../images/world-authoring-terrain-palette.webp';
import froggyWorldMenuImage from '../../images/froggyworld-main-menu.webp';
import oceanCleanupImage from '../../images/froggyworld-ocean-cleanup.webp';
import oceanCleanupReefImage from '../../images/froggyworld-ocean-cleanup-reef.webp';
import './PortfolioPages.css';

const projectGalleries = {
  'world-authoring-lab': {
    sections: [
      {
        heading: 'Generated terrain that stays editable.',
        description: 'The Terrain Lab exposes terrain layers, recipe regions, seeds, palette choices, camera views, and sculpting controls instead of collapsing the result into a single image.',
        items: [
          {
            src: terrainLandscapeImage,
            alt: 'Teal valleys and purple mountain ridges in the Terrain Lab, with sculpting controls visible.',
            caption: 'A generated landscape with teal valleys, purple ridges, and sculpting tools for shaping the result.',
            width: 1613, height: 808
          },
          {
            src: terrainPaletteImage,
            alt: 'The Terrain Lab showing gold, green, and purple terrain with the Chaos / full palette preset.',
            caption: 'The full-palette generator produces a different terrain character, with the recipe and editing controls alongside it.',
            width: 1911, height: 907
          },
          {
            src: terrainWorkbenchImage,
            alt: 'The Terrain Lab with controls for random generation, recipe regions, saved terrain, and camera views.',
            caption: 'Natural ranges: generate a starting point, adjust its regions, and switch between landscape and ground-level views.',
            width: 1907, height: 838
          }
        ]
      },
      {
        heading: 'From authoring tools to a world.',
        description: 'The same system includes playable worlds such as Ocean Cleanup, plus scene tools for composing creatures, procedural plants, materials, and cinematic light.',
        items: [
          {
            src: oceanCleanupImage,
            alt: 'Ocean Cleanup gameplay with a sea turtle, schools of fish, and a floating milk carton among underwater rocks and plants.',
            caption: 'Ocean Cleanup: a sea turtle approaches a drifting milk carton while fish swim through the underwater landscape.',
            width: 1455, height: 838
          },
          {
            src: oceanCleanupReefImage,
            alt: 'An overhead view of the Ocean Cleanup turtle swimming past aquatic plants and a school of fish.',
            caption: 'A wider view of the turtle, fish, plants, and patterned seafloor.',
            width: 1473, height: 845
          },
          {
            src: froggyWorldMenuImage,
            alt: 'The Froggy World main menu with choices for Ocean Cleanup, Trail Dog, Frog Conveyor, and Claw Game.',
            caption: 'The Froggy World menu links Ocean Cleanup, Trail Dog, Frog Conveyor, and Claw Game.',
            width: 808, height: 577
          }
        ]
      }
    ]
  }
};

function ThemeLinks({ slugs = [], dark = false }) {
  const selectedThemes = slugs.map((slug) => themes.find((theme) => theme.slug === slug)).filter(Boolean);
  if (!selectedThemes.length) return null;
  return (
    <ul className={'pf-detail-tags' + (dark ? ' pf-detail-tags-dark' : '')} aria-label="Themes">
      {selectedThemes.map((theme) => (
        <li key={theme.slug}>
          <span>{theme.title}</span>
        </li>
      ))}
    </ul>
  );
}

function Breadcrumbs({ items }) {
  return (
    <nav className="pf-breadcrumbs" aria-label="Breadcrumb">
      <ol>
        <li><Link to="/"><span aria-hidden="true">←</span> Home</Link></li>
        {items.map((item, index) => (
          <li key={index}>
            <span className="pf-breadcrumb-divider" aria-hidden="true">/</span>
            {item.to ? <Link to={item.to}>{item.label}</Link> : <span aria-current="page">{item.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function ProjectPage() {
  const { slug } = useParams();
  const { pathname, search } = useLocation();
  const project = getProject(slug);
  if (!project) return <NotFoundPage kind="project" />;

  const projectNotes = getProjectNotes(project.slug);
  const otherProjects = projects.filter((item) => item.slug !== project.slug && item.showOnHome !== false).slice(0, 2);
  const gallery = projectGalleries[project.slug];

  return (
    <PortfolioShell className={'pf-project-page pf-accent-' + (project.accent || 'cyan')}>
      <header className="pf-detail-hero">
        <div className="portfolio-container">
          <Breadcrumbs items={[{ label: 'Projects', to: '/#showcase' }, { label: project.title }]} />
          <p className="pf-eyebrow">{project.kicker}</p>
          <h1>{project.title}</h1>
          <p className="pf-project-heading">{project.heading}</p>
          <p className="pf-detail-lead">{project.summary}</p>
          <ThemeLinks slugs={project.themeSlugs} dark />
          {project.slug === 'world-authoring-lab' && (
            <div className="pf-project-demo-actions">
              <a href="https://froggy.dunelabs.io" target="_blank" rel="noreferrer">Open Froggy World demo <span aria-hidden="true">↗</span></a>
            </div>
          )}
        </div>
      </header>

      <div className="portfolio-container pf-detail-content">
        <div className="pf-project-opening">
          <div className="pf-project-intro">
            {project.body.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          </div>
          <nav className="pf-project-map" aria-label={project.title + ' sections'}>
            <p>Inside the project</p>
            {gallery && <Link to={{ pathname, search, hash: '#project-gallery' }}>Scenes &amp; editor <span aria-hidden="true">↘</span></Link>}
            <Link to={{ pathname, search, hash: '#project-problem' }}>Why <span aria-hidden="true">↘</span></Link>
            <Link to={{ pathname, search, hash: '#project-approach' }}>How it works <span aria-hidden="true">↘</span></Link>
            <Link to={{ pathname, search, hash: '#project-evidence' }}>Tools in use <span aria-hidden="true">↘</span></Link>
            <Link to={{ pathname, search, hash: '#project-notes' }}>Working notes <span aria-hidden="true">↘</span></Link>
          </nav>
        </div>

        {gallery && (
          <div className="pf-project-gallery" id="project-gallery">
            {gallery.sections.map((section, sectionIndex) => {
              const headingId = 'project-gallery-heading-' + sectionIndex;
              return (
                <section className="pf-project-gallery-section" aria-labelledby={headingId} key={section.heading}>
                  <div className="pf-project-gallery-heading">
                    <p className="pf-eyebrow">{sectionIndex === 0 ? 'In the editor' : 'In the world'}</p>
                    <h2 id={headingId}>{section.heading}</h2>
                    <p>{section.description}</p>
                  </div>
                  <div className="pf-project-gallery-grid">
                    {section.items.map((item, index) => (
                      <figure className={index === 0 ? 'pf-project-gallery-feature' : undefined} key={item.src}>
                        <img src={item.src} alt={item.alt} width={item.width} height={item.height} loading={sectionIndex === 0 && index === 0 ? 'eager' : 'lazy'} decoding="async" />
                        <figcaption>{item.caption}</figcaption>
                      </figure>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <section className="pf-project-problem" id="project-problem" aria-labelledby="project-problem-heading">
          <p className="pf-eyebrow">Why?</p>
          <h2 id="project-problem-heading">The starting point</h2>
          <p>{project.problem}</p>
        </section>

        <div className="pf-project-method">
          <section id="project-approach" aria-labelledby="project-approach-heading">
            <p className="pf-eyebrow">What the tool does</p>
            <h2 id="project-approach-heading">How it works</h2>
            <ol className="pf-approach-list">
              {project.approach.map((item, index) => (
                <li key={index}><span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span><p>{item}</p></li>
              ))}
            </ol>
          </section>
          <section className="pf-project-explores" aria-labelledby="project-explores-heading">
            <p className="pf-eyebrow">In practice</p>
            <h2 id="project-explores-heading">What it helps with</h2>
            <ul>{project.demonstrates.map((item, index) => <li key={index}>{item}</li>)}</ul>
          </section>
        </div>

        <aside className="pf-open-question" aria-labelledby="project-open-question-heading">
          <p className="pf-eyebrow">Still exploring</p>
          <h2 id="project-open-question-heading">The unfinished part</h2>
          <p>{project.openQuestion}</p>
        </aside>

        <section className="pf-project-evidence" id="project-evidence" aria-labelledby="project-evidence-heading">
          <div className="pf-detail-section-heading">
            <div><p className="pf-eyebrow">Technology with a job to do</p><h2 id="project-evidence-heading">Tools in use</h2></div>
            <Link className="pf-text-link" to="/background">More about my background <span aria-hidden="true">→</span></Link>
          </div>
          <dl className="pf-evidence-grid">
            {project.technologyEvidence.map((technology) => (
              <div key={technology.name}><dt>{technology.name}</dt><dd>{technology.detail}</dd></div>
            ))}
          </dl>
        </section>

        <section className="pf-project-notes" id="project-notes" aria-labelledby="project-notes-heading">
          <div className="pf-detail-section-heading">
            <div><p className="pf-eyebrow">Questions behind the work</p><h2 id="project-notes-heading">Working notes</h2></div>
          </div>
          <NoteViews notes={projectNotes} label={project.title + ' notes'} />
        </section>

        <section className="pf-keep-reading" aria-labelledby="project-next-heading">
          <p className="pf-eyebrow">Keep exploring</p>
          <h2 id="project-next-heading">Related work</h2>
          <div className="pf-related-grid">
            {otherProjects.map((item) => (
              <Link className="pf-related-card" to={'/projects/' + item.slug} key={item.slug}>
                <span className="pf-related-label">Project</span><h3>{item.title}</h3><p>{item.summary}</p><span className="pf-related-action">Read the project <span aria-hidden="true">→</span></span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </PortfolioShell>
  );
}

export function NotesPage() {
  const featuredNotes = notes.filter((note) => note.featured);

  return (
    <PortfolioShell className="pf-notes-page">
      <header className="pf-detail-hero pf-notes-hero">
        <div className="portfolio-container">
          <Breadcrumbs items={[{ label: 'Notes' }]} />
          <p className="pf-eyebrow">Working notes</p>
          <h1>Three ideas I keep returning to.</h1>
          <p className="pf-detail-lead">Short notes that explain how I think about personal tools, agents, and work that still needs to function when the best model is not available.</p>
        </div>
      </header>
      <div className="portfolio-container pf-detail-content">
        <section aria-labelledby="notes-browse-heading">
          <div className="pf-detail-section-heading">
            <div><h2 id="notes-browse-heading">Working notes</h2></div>
          </div>
          <NoteViews notes={featuredNotes} label="Working notes" />
        </section>
      </div>
    </PortfolioShell>
  );
}

export function NotePage() {
  const { slug } = useParams();
  const { state } = useLocation();
  const note = getNote(slug);
  if (!note) return <NotFoundPage kind="note" />;

  const originProjectSlug = state?.noteReturn?.projectSlug;
  const originProject = originProjectSlug ? getProject(originProjectSlug) : null;
  const notesReturn = originProject
    ? { pathname: '/projects/' + originProject.slug, hash: '#project-notes' }
    : { pathname: '/notes' };
  const breadcrumbItems = originProject
    ? [{ label: 'Projects', to: '/#showcase' }, { label: originProject.title, to: '/projects/' + originProject.slug }, { label: note.title }]
    : [{ label: 'Notes', to: notesReturn }, { label: note.title }];

  return (
    <PortfolioShell className="pf-single-note-page">
      <div className="portfolio-container pf-note-page-content">
        <Breadcrumbs items={breadcrumbItems} />
        <article className="pf-note-article pf-note-article-simple">
          <p className="pf-eyebrow">{note.stage || 'Working note'}</p>
          <h1>{note.title}</h1>
          <div className="pf-note-prose">{note.body.map((paragraph, index) => <p key={index}>{paragraph}</p>)}</div>
          <ThemeLinks slugs={note.themeSlugs} />
          <Link className="pf-text-link pf-back-notes" to={notesReturn}><span aria-hidden="true">←</span> {originProject ? 'Back to ' + originProject.title + ' notes' : 'Back to working notes'}</Link>
        </article>
      </div>
    </PortfolioShell>
  );
}

export function BackgroundPage() {
  return (
    <PortfolioShell className="pf-background-page">
      <header className="pf-detail-hero">
        <div className="portfolio-container">
          <Breadcrumbs items={[{ label: 'Background' }]} />
          <p className="pf-eyebrow">Software design &amp; architecture</p>
          <h1>Software that takes fewer steps to use and less attention to run.</h1>
          <p className="pf-detail-lead">I build software, automation, and agentic systems that reduce the amount of work it takes to get useful things done.</p>
          <div className="pf-background-hero-actions">
            <a className="pf-button pf-button-primary" href="https://www.linkedin.com/in/rpmiller4" target="_blank" rel="noreferrer">Full work history on LinkedIn <span aria-hidden="true">↗</span></a>
          </div>
        </div>
      </header>
      <div className="portfolio-container pf-detail-content">
        {skillGroups.map((group) => (
          <section className="pf-skill-section" id={group.id} aria-labelledby={group.id + '-heading'} key={group.id}>
            <div className="pf-detail-section-heading">
              <div>
                <p className="pf-eyebrow">{group.origin || 'Skill set'}</p>
                <h2 id={group.id + '-heading'}>{group.title}</h2>
              </div>
            </div>
            <dl className="pf-skill-list">
              {group.items.map((item) => (
                <div key={item.title}>
                  <dt>{item.title}</dt>
                  <dd>{item.description}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
        <section className="pf-background-contact" aria-labelledby="background-contact-heading">
          <div><p className="pf-eyebrow">Continue the conversation</p><h2 id="background-contact-heading">Have a problem worth exploring?</h2><p>Projects, architecture, and practical AI systems are good places to start.</p></div>
          <Link className="pf-button pf-button-primary" to="/connect">Let’s connect <span aria-hidden="true">→</span></Link>
        </section>
      </div>
    </PortfolioShell>
  );
}

export function NotFoundPage({ kind = 'page' }) {
  return (
    <PortfolioShell className="pf-not-found-page">
      <section className="portfolio-container pf-not-found">
        <Breadcrumbs items={[{ label: 'Page not found' }]} />
        <p className="pf-eyebrow">404 · A missing connection</p>
        <h1>This {kind} isn’t here.</h1>
        <p>The address may have changed. The project collection and working notes are good places to pick up the thread.</p>
        <div className="pf-not-found-actions"><Link className="pf-button pf-button-primary" to="/#showcase">Explore the projects <span aria-hidden="true">→</span></Link><Link className="pf-text-link" to="/notes">Read the notes</Link></div>
      </section>
    </PortfolioShell>
  );
}
